import { useEffect, useState } from "react";
import { Copy, Send, Users, CheckCircle2, Circle, Gift } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const FRONTEND_ORIGIN = typeof window !== "undefined" ? window.location.origin : "";

export default function ReferralsPage() {
  const [data, setData] = useState(null);
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);

  const load = async () => {
    try {
      const { data: d } = await api.get("/referrals/me");
      setData(d);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => { load(); }, []);

  const shareUrl = data ? `${FRONTEND_ORIGIN}/register?ref=${data.code}` : "";

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Referral link copied");
    } catch {
      toast.error("Copy failed — select and copy manually");
    }
  };

  const trackInvite = async () => {
    if (!email) return;
    setSending(true);
    try {
      await api.post("/referrals/invite", { email, note });
      toast.success(`Tracked invite to ${email}`);
      setEmail(""); setNote("");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSending(false);
    }
  };

  const shareMailto = () => {
    if (!email) return;
    const subj = encodeURIComponent("Try HomeSchool Hub with me");
    const body = encodeURIComponent(
      `Hey!\n\nI've been using HomeSchool Hub to manage our Florida homeschool — the evaluation packet is a game-changer.\n\nSign up with my link and we both get a bonus month:\n${shareUrl}\n\n— ${note ? note + "\n\n" : ""}`
    );
    window.open(`mailto:${email}?subject=${subj}&body=${body}`);
    trackInvite();
  };

  if (!data) return <div className="text-stone-500" data-testid="referrals-loading">Loading…</div>;

  const stat = ({ icon: Icon, label, value, tone }) => (
    <div className={`rounded-2xl border p-5 ${tone}`}>
      <div className="text-xs uppercase tracking-[0.15em] font-semibold text-stone-500 flex items-center gap-2"><Icon className="w-4 h-4" /> {label}</div>
      <div className="mt-2 font-display text-4xl font-semibold text-stone-900">{value}</div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="referrals-page">
      <div>
        <h1 className="font-display text-3xl md:text-4xl font-semibold">Co-op leader referrals</h1>
        <p className="text-stone-500 mt-1 text-sm max-w-2xl">
          Invite fellow homeschool families &mdash; when they sign up with your link, both accounts earn a free month of Premium.
        </p>
      </div>

      <div className="grid md:grid-cols-4 gap-4">
        {stat({ icon: Send, label: "Invited", value: data.invited, tone: "bg-white border-stone-200" })}
        {stat({ icon: Users, label: "Signed up", value: data.signed_up, tone: "bg-amber-50 border-amber-200" })}
        {stat({ icon: CheckCircle2, label: "Activated Premium", value: data.activated, tone: "bg-emerald-50 border-emerald-200" })}
        {stat({ icon: Gift, label: "Bonus months earned", value: data.bonus_months || 0, tone: "bg-gradient-to-br from-violet-50 to-fuchsia-50 border-violet-200" })}
      </div>
      {(data.bonus_months || 0) > 0 && (
        <div className="rounded-2xl bg-violet-50 border border-violet-200 text-violet-900 text-sm p-4 flex items-center gap-3" data-testid="bonus-credit-banner">
          <Gift className="w-5 h-5 text-violet-600" />
          <span>You have <strong>{data.bonus_months}</strong> bonus month{data.bonus_months === 1 ? "" : "s"} in credit. They&apos;ll apply automatically on your next Premium checkout.</span>
        </div>
      )}

      <div className="bg-white border border-stone-200 rounded-2xl p-6">
        <div className="text-xs uppercase tracking-[0.15em] font-semibold text-stone-500">Your personal referral link</div>
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <code className="flex-1 min-w-0 truncate rounded-xl bg-stone-100 border border-stone-200 px-4 py-3 text-sm font-mono text-stone-800" data-testid="referral-url">
            {shareUrl}
          </code>
          <Button onClick={copyLink} className="rounded-full bg-amber-500 hover:bg-amber-600 text-white" data-testid="referral-copy">
            <Copy className="w-4 h-4 mr-2" /> Copy
          </Button>
        </div>
        <div className="mt-2 text-xs text-stone-500">Code: <strong className="text-stone-800">{data.code}</strong></div>
      </div>

      <div className="bg-white border border-stone-200 rounded-2xl p-6">
        <h3 className="font-display text-lg font-semibold">Invite a family</h3>
        <p className="text-sm text-stone-500 mt-1">We&apos;ll open your email client with a pre-written invite and track the send here.</p>
        <div className="mt-4 grid md:grid-cols-3 gap-3">
          <div className="md:col-span-1">
            <Label>Their email</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="friend@example.com" className="mt-2 rounded-xl h-11" data-testid="referral-email" />
          </div>
          <div className="md:col-span-2">
            <Label>Personal note (optional)</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Loved how it made evaluator prep effortless — thought of you!" className="mt-2 rounded-xl h-11" data-testid="referral-note" />
          </div>
        </div>
        <div className="mt-4 flex items-center gap-2">
          <Button onClick={shareMailto} disabled={!email || sending} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="referral-invite-mailto">
            <Send className="w-4 h-4 mr-2" /> Share via email
          </Button>
          <Button variant="outline" onClick={trackInvite} disabled={!email || sending} className="rounded-full border-2 border-stone-200" data-testid="referral-track-only">
            Just track this invite
          </Button>
        </div>
      </div>

      {data.referrals.length > 0 && (
        <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 text-stone-500 uppercase tracking-[0.12em] text-[11px]">
              <tr>
                <th className="text-left px-4 py-3">Email</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Sent</th>
                <th className="text-left px-4 py-3">Note</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {data.referrals.map((r) => (
                <tr key={r.id} data-testid={`referral-row-${r.id}`}>
                  <td className="px-4 py-3 font-medium text-stone-900">{r.email}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ring-1 ${r.status === "activated" ? "bg-emerald-50 text-emerald-800 ring-emerald-200" : r.status === "signed_up" ? "bg-amber-50 text-amber-800 ring-amber-200" : "bg-stone-50 text-stone-600 ring-stone-200"}`}>
                      {r.status === "activated" ? <CheckCircle2 className="w-3 h-3" /> : <Circle className="w-3 h-3" />} {r.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-stone-500">{(r.created_at || "").slice(0, 10)}</td>
                  <td className="px-4 py-3 text-stone-500 truncate max-w-xs">{r.note || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
